'use client';
import { Module } from '~/lib';
import React, { FC, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { N20MainMenu } from '~/features/navigation';
import N60Footer from '~/features/navigation/components/N60Footer';
import Breadcrumbs from '~/shared/components/Breadcrumb/Breadcrumb';
import { useFrame } from '~/shared/utils';
import { theme } from '~/theme';
import { MetaFields } from '../../../MetaFields';
import { TopButton } from '../../../TopButton/TopButton';
import { StyledMain } from './styled';
import dynamic from 'next/dynamic';
import { useSessionStorage } from 'react-use';
import { useDynamicHooks } from '../../hooks/useDynamicHooks';
import { useGoogleTagManagerScript } from '~/shared/scripts/useGoogleTagManager';
import { usePage } from '~/templates/pages/hooks/usePage';
import { StandardPageSchema } from '~/features/standard-page/components/StandardPageSchema';
import { useIsMobile } from '~/shared/hooks/useIsMobile/useIsMobile';
import { getQueryValue } from '~/shared/utils/get-query-value';

const Scripts = dynamic(() => import('../Scripts'), {
    ssr: false,
});

const LoginDrawer = dynamic(() => import('../LoginDrawer'), {
    ssr: false,
});

const BasketDrawer = dynamic(() => import('../BasketDrawer'), {
    ssr: false,
});

const Toast = dynamic(() => import('../Toast'), {
    ssr: false,
});

// Mounted at layout level so a comparison follows the customer from page to page. Checkout
// uses its own layout, so it never sees the tray.
const ProductComparisonTray = dynamic(() => import('~/shared/components/ProductComparisonTray'), {
    ssr: false,
});

const Standard: FC = ({ children }) => {
    const { data } = useFrame();
    const page = usePage();
    const isMobile = useIsMobile();
    const { query: routerQuery } = useRouter();

    const [originalLocation, setOriginalLocation] = useSessionStorage('originalLocation');

    useGoogleTagManagerScript({
        frame: data,
        page: page,
    });

    useDynamicHooks();

    useEffect(() => {
        setOriginalLocation(originalLocation || window.location.href);
    }, []);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { breadcrumb, heroHeader, heroTheme, pageElements, type: pageType } = usePage();

    const firstModule = pageElements?.[0] as Module | undefined;

    const firstModuleIsHero =
        !!firstModule?.type &&
        ['m10HeroModule', 'm70MediaModule', 'm75MediaAndTextModule'].includes(firstModule.type);

    const breadcrumbsShouldFloat =
        pageType !== 'p40ProductListPage' &&
        (firstModuleIsHero ||
            !!heroHeader ||
            pageType === 'p160ArticlePage' ||
            pageType === 'giftCardBalancePage');

    let breadcrumbsColor = (heroTheme === 'Light' ? theme.colors.white : theme.colors.black) as
        | typeof theme.colors.white
        | typeof theme.colors.black;

    if (!heroHeader && firstModuleIsHero) {
        breadcrumbsColor =
            firstModule?.textColor?.toUpperCase() === theme.colors.white
                ? theme.colors.white
                : theme.colors.black;
    }

    if (firstModuleIsHero && firstModule) {
        firstModule.spacingTop = 'none';
    }

    const shouldShowBreadcrumbs = pageType !== 'brandsListPage' && pageType !== 'p125MyProfilePage';

    const searchTerm = getQueryValue(routerQuery.query);

    const breadcrumbItems = useMemo(() => {
        if (pageType === 'p10SearchPage' && searchTerm) {
            const items = breadcrumb?.items ?? [];
            // Søgeresultater shouldn't be clickable once the search term is appended after it
            const itemsWithoutLastUrl = items.map((item, i) =>
                i === items.length - 1 ? { ...item, url: undefined } : item,
            );

            return [...itemsWithoutLastUrl, { text: searchTerm }];
        }

        return breadcrumb?.items;
    }, [breadcrumb?.items, pageType, searchTerm]);

    return (
        <>
            <MetaFields />
            <Scripts />

            {page.meta?.schemaMarkup && (
                <StandardPageSchema schemaMarkup={page.meta?.schemaMarkup} />
            )}

            <N20MainMenu showTopBanner={!!data.header?.topBanner} />

            <StyledMain id="mainContent" tabIndex={-1}>
                {shouldShowBreadcrumbs && breadcrumbItems && breadcrumbItems.length > 1 && (
                    <Breadcrumbs
                        items={breadcrumbItems}
                        shouldFloat={breadcrumbsShouldFloat}
                        color={breadcrumbsShouldFloat ? breadcrumbsColor : undefined}
                    />
                )}

                {children}

                <LoginDrawer />
                <BasketDrawer />
                <Toast />
                <ProductComparisonTray />

                {isMobile && <TopButton />}
            </StyledMain>

            <N60Footer />
        </>
    );
};

export default Standard;
